import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiChevronRight,
  FiChevronLeft,
  FiHome,
  FiSearch,
  FiDownload,
} from "react-icons/fi";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { getAllFundTransfers } from "../../store/slices/withdraw";

const WalletTransfer = () => {
  const dispatch = useDispatch();

  const {
    fundTransfers = [],
    fundTransfersLoading,
    fundTransfersError,
    fundTransfersPagination,
  } = useSelector((state) => state.withdraw);


  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);

  const totalTransfers =
    fundTransfersPagination?.totalTransfers ||
    fundTransfersPagination?.total ||
    0;

  const totalPages =
    fundTransfersPagination?.totalPages || 0;

  const currentPage =
    fundTransfersPagination?.currentPage || page;

  const hasNextPage =
    fundTransfersPagination?.hasNextPage ||
    currentPage < totalPages;

  const hasPreviousPage =
    fundTransfersPagination?.hasPreviousPage ||
    currentPage > 1;

  useEffect(() => {
    dispatch(
      getAllFundTransfers({
        page,
        limit,
        search: search.trim(),
      })
    );
  }, [dispatch, page, limit, search]);

  // ================= DATE =================

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ================= PAGE NUMBERS =================

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        pages.push(
          1,
          2,
          3,
          4,
          "...",
          totalPages
        );
      } else if (page >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          page - 1,
          page,
          page + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  // ================= PAGINATION HANDLERS =================

  const handlePrevious = () => {
    if (hasPreviousPage) {
      setPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= totalPages &&
      pageNumber !== page
    ) {
      setPage(pageNumber);
    }
  };

  // ================= DISPLAY RANGE =================

  const displayStart =
    totalTransfers > 0
      ? (page - 1) * limit + 1
      : 0;

  const displayEnd =
    Math.min(
      page * limit,
      totalTransfers
    );

  const handleDownloadPDF = async () => {
    try {
      setDownloadLoading(true);

      const response = await dispatch(
        getAllFundTransfers({
          page: 1,
          limit: 100000,
          search: search.trim(),
        })
      ).unwrap();

      const exportList =
        response?.data?.fundTransfers ||
        response?.data?.transfers ||
        response?.fundTransfers ||
        response?.transfers ||
        [];

      if (!exportList.length) {
        alert("No transfer records available.");
        return;
      }

      const doc = new jsPDF(
        "landscape",
        "mm",
        "a4"
      );

      doc.setFontSize(16);

      doc.text(
        "Wallet Transfer List",
        14,
        15
      );

      if (search.trim()) {
        doc.setFontSize(9);

        doc.text(
          `Search: ${search.trim()}`,
          14,
          21
        );
      }

      const tableData = exportList.map(
        (item, index) => {

          const fromUser =
            item?.sourceUserId ||
            item?.fromUserId ||
            item?.senderId ||
            {};

          const toUser =
            item?.receiverUserId ||
            item?.toUserId ||
            item?.receiverId ||
            {};

          const fromName =
            typeof fromUser === "object"
              ? fromUser?.name
              : "-";

          const fromId =
            typeof fromUser === "object"
              ? fromUser?.userId
              : fromUser;

          const toName =
            typeof toUser === "object"
              ? toUser?.name
              : "-";

          const toId =
            typeof toUser === "object"
              ? toUser?.userId
              : toUser;

          const charges =
            Number(
              item?.charges ??
              item?.charge ??
              item?.transferCharges ??
              0
            );

          const amount =
            Number(item?.amount) || 0;

          const status =
            item?.status || "PENDING";

          return [
            index + 1,
            `${fromId || "-"}\n${fromName || "-"}`,
            `${toId || "-"}\n${toName || "-"}`,
            `$${charges.toFixed(2)}`,
            `$${amount.toFixed(2)}`,
            formatDate(item?.createdAt),
            status,
          ];
        }
      );

      autoTable(doc, {
        startY: search.trim() ? 27 : 22,

        head: [[
          "#",
          "From",
          "To",
          "Charges",
          "Amount",
          "Date",
          "Status",
        ]],

        body: tableData,

        theme: "grid",

        styles: {
          fontSize: 8,
          cellPadding: 3,
        },

        headStyles: {
          fontSize: 8,
          fontStyle: "bold",
        },

        columnStyles: {
          0: {
            cellWidth: 12,
          },
          3: {
            cellWidth: 25,
          },
          4: {
            cellWidth: 25,
          },
          5: {
            cellWidth: 30,
          },
          6: {
            cellWidth: 25,
          },
        },
      });

      doc.save(
        "wallet-transfer-list.pdf"
      );

    } catch (error) {

      console.error(
        "PDF download failed:",
        error
      );

      alert(
        "Failed to download PDF."
      );

    } finally {

      setDownloadLoading(false);
    }
  };

  return (
    <div className="gi-referral-page">

      {/* ================= BREADCRUMB ================= */}

      <div className="gi-breadcrumb">

        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>
          Wallet
        </span>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>
          Wallet Transfer List
        </span>

      </div>

      {/* ================= TABLE ================= */}

      <section className="gi-referral-table-card">
        {/* ================= FILTER BAR ================= */}

        <div className="gi-users-filter">

          {/* SEARCH */}

          {/* PER PAGE */}

          <div className="gi-users-page-size me-2">
            <label htmlFor="pageSize">
              Show
            </label>

            <select
              id="pageSize"
              value={limit}
              onChange={(e) => {
                const newLimit = Number(e.target.value);

                setLimit(newLimit);
                setPage(1);
              }}
              disabled={fundTransfersLoading}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <span>
              per page
            </span>
          </div>

          <div className="gi-users-search me-2">
            <FiSearch />

            <input
              type="text"
              placeholder="Search transfers..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>




          {/* DOWNLOAD */}

          <button
            type="button"
            className="gi-user-export-btn"
            onClick={handleDownloadPDF}
            disabled={
              downloadLoading ||
              fundTransfersLoading ||
              fundTransfers.length === 0
            }
          >
            <FiDownload />

            <span>
              {downloadLoading
                ? "Downloading..."
                : "Download PDF"}
            </span>
          </button>

        </div>
        <div className="gi-referral-table-wrapper">

          <table className="gi-referral-table">

            <thead>
              <tr>

                <th>#</th>

                <th>
                  From
                </th>

                <th className="text-center">
                  To
                </th>

                <th className="text-center">
                  Charges
                </th>

                <th className="text-center">
                  Amount
                </th>

                <th className="text-center">
                  Date
                </th>

                <th className="text-center">
                  Status
                </th>

              </tr>
            </thead>

            <tbody>

              {/* ================= LOADING ================= */}

              {fundTransfersLoading && (
                <tr>
                  <td
                    colSpan="7"
                    className="gi-table-message"
                  >
                    Loading payments...
                  </td>
                </tr>
              )}

              {/* ================= ERROR ================= */}

              {!fundTransfersLoading &&
                fundTransfersError && (
                  <tr>
                    <td
                      colSpan="7"
                      className="gi-table-message"
                    >
                      {fundTransfersError}
                    </td>
                  </tr>
                )}

              {/* ================= EMPTY ================= */}

              {!fundTransfersLoading &&
                !fundTransfersError &&
                fundTransfers.length === 0 && (
                  <tr>
                    <td
                      colSpan="7"
                      className="gi-table-message"
                    >
                      No payments found
                    </td>
                  </tr>
                )}

              {/* ================= DATA ================= */}

              {!fundTransfersLoading &&
                !fundTransfersError &&
                fundTransfers.map((item, index) => {

                  const fromUser =
                    item?.sourceUserId ||
                    item?.fromUserId ||
                    item?.senderId ||
                    {};

                  const toUser =
                    item?.receiverUserId ||
                    item?.toUserId ||
                    item?.receiverId ||
                    {};

                  const fromName =
                    typeof fromUser === "object"
                      ? fromUser?.name
                      : "-";

                  const fromId =
                    typeof fromUser === "object"
                      ? fromUser?.userId
                      : fromUser;

                  const toName =
                    typeof toUser === "object"
                      ? toUser?.name
                      : "-";

                  const toId =
                    typeof toUser === "object"
                      ? toUser?.userId
                      : toUser;

                  const status =
                    item?.status ||
                    "PENDING";

                  const amount =
                    Number(item?.amount) || 0;

                  const charges =
                    Number(
                      item?.charges ??
                      item?.charge ??
                      item?.transferCharges ??
                      0
                    );

                  return (
                    <tr
                      key={
                        item?._id ||
                        index
                      }
                    >

                      {/* SERIAL */}

                      <td className="gi-referral-number">

                        {String(
                          (page - 1) * limit +
                          index +
                          1
                        ).padStart(2, "0")}

                      </td>

                      {/* FROM */}

                      <td>

                        <div>

                          <strong>
                            {fromId || "-"}
                          </strong>

                          <br />

                          <small>
                            {fromName || "-"}
                          </small>

                        </div>

                      </td>

                      {/* TO */}

                      <td>

                        <strong>
                          {toId || "-"}
                        </strong>

                        <br />

                        <small>
                          {toName || "-"}
                        </small>

                      </td>

                      {/* CHARGES */}

                      <td className="text-center">

                        <strong className="gi-payment-amount">
                          ${charges}
                        </strong>

                      </td>

                      {/* AMOUNT */}

                      <td className="text-center">

                        <strong className="gi-payment-amount">
                          ${amount}
                        </strong>

                      </td>

                      {/* DATE */}

                      <td className="text-center">

                        <span className="gi-date-text">
                          {formatDate(
                            item?.createdAt
                          )}
                        </span>

                      </td>

                      {/* STATUS */}

                      <td className="text-center">

                        <span
                          className={`gi-status-badge ${String(
                            status
                          ).toLowerCase()}`}
                        >
                          {status}
                        </span>

                      </td>

                    </tr>
                  );
                })}

            </tbody>

          </table>

        </div>

        {/* ================= PAGINATION ================= */}

        {!fundTransfersLoading &&
          !fundTransfersError &&
          totalPages > 0 && (

            <div className="gi-users-pagination">

              {/* PAGINATION INFO */}

              <div className="gi-pagination-info">

                Showing{" "}

                <strong>
                  {displayStart}
                </strong>

                {" "}to{" "}

                <strong>
                  {displayEnd}
                </strong>

                {" "}of{" "}

                <strong>
                  {totalTransfers}
                </strong>

                {" "}transfers

              </div>

              {/* PAGINATION BUTTONS */}

              <div className="gi-pagination-buttons">

                {/* PREVIOUS */}

                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={handlePrevious}
                  disabled={!hasPreviousPage}
                >

                  <FiChevronLeft />

                  <span>
                    Prev
                  </span>

                </button>

                {/* PAGE NUMBERS */}

                {getPageNumbers().map(
                  (pageNumber, index) => {

                    if (
                      pageNumber === "..."
                    ) {
                      return (
                        <span
                          key={`dots-${index}`}
                          className="gi-pagination-dots"
                        >
                          ...
                        </span>
                      );
                    }

                    return (
                      <button
                        type="button"
                        key={pageNumber}
                        className={`gi-pagination-number ${page === pageNumber
                          ? "active"
                          : ""
                          }`}
                        onClick={() =>
                          handlePageChange(
                            pageNumber
                          )
                        }
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                )}

                {/* NEXT */}

                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={handleNext}
                  disabled={!hasNextPage}
                >

                  <span>
                    Next
                  </span>

                  <FiChevronRight />

                </button>

              </div>

            </div>
          )}

      </section>

    </div>
  );
};

export default WalletTransfer;
